import { styled, keyframes } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { Person } from '@mui/icons-material';
import { Table, TableRow, Box, Typography, Paper, TableCell, Pagination } from '@mui/material';
import { getContrastColor } from '#component/theme';

// User-popup component

const blink = keyframes`
  from { opacity: 1; }
  to { opacity: 0.2; }
`;

export const Content = styled('div')(({ theme }) => ({
  height: 'calc(100% - 227px)',

  [theme.breakpoints.down('sm')]: {
    height: 'calc(100% - 240px)'
  }
}));

export const HeaderActions = styled('div')(({ theme }) => ({
  position: 'sticky',
  display: 'grid',
  gridTemplateColumns: '235px 163px 190px',
  gridTemplateAreas: '"switch filter addBtn"',
  gap: theme.spacing(3),
  padding: theme.spacing(2, 4),
  width: '100%',
  zIndex: 1100,

  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.25),
    padding: theme.spacing(2),
    gridTemplateAreas: '"switch addBtn" "filter filter"',
    gridTemplateColumns: '200px auto'
  }
}));

export const HeaderTitle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3, 4, 0, 4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2, 0)
  }
}));

export const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  height: '90%',
  backgroundColor: '#fff',
  borderRadius: 8,
  boxShadow: 24,
  overflow: 'hidden',
  outline: 'none',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '100%',
    borderRadius: 0
  }
}));

export const PaginationContainer = styled('div')(() => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: 48,
  backgroundColor: '#fff',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow:
    '0px 1px 12px rgb(0 0 0 / 11%),0px 2px 14px rgb(0 0 0 / 5%),0px 1px 18px rgb(0 0 0 / 8%)'
}));

export const PaginationCount = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  color: '#828282',
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

export const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1,

  '&:hover': {
    cursor: 'pointer'
  }
}));

export const StyledHeader = styled('div')(() => ({
  position: 'relative',
  boxShadow:
    '0px 1px 12px rgb(0 0 0 / 11%), 0px 2px 14px rgb(0 0 0 / 5%), 0px 1px 18px rgb(0 0 0 / 8%)'
}));

export const StyledPagination = styled(Pagination)(({ theme }) => ({
  padding: theme.spacing(1, 0, 1, 0),
  '& > ul': {
    justifyContent: 'center'
  }
}));

export const StyledTitle = styled(Typography)(() => ({
  fontWeight: 500
}));

export const SubTitle = styled(Typography)(() => ({
  minHeight: 24
}));

// Table component

export const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 4, 2, 3.5),
  boxShadow: 'none',
  maxHeight: '100%',
  overflowY: 'auto',
  '&::-webkit-scrollbar-track': {
    WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
    borderRadius: 10,
    backgroundColor: '#F5F5F5'
  },
  '&::-webkit-scrollbar': {
    width: 8,
    backgroundColor: '#F5F5F5'
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 10,
    WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,.3)',
    backgroundColor: '#a0a0a0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2.25, 3)
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.25)
  },
  borderRadius: 0
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: 0,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 0, 0.75, 1.5)
  },
  '&:last-child': {
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  },
  '&:hover': {
    cursor: 'pointer'
  },
  '&[scope="row"]': {
    height: 44
  }
}));

export const StyledTableCheckBox = styled(TableCell)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 1.5)
  }
}));

export const StyledTable = styled(Table, {
  shouldForwardProp: prop => prop !== 'loading'
})(({ theme, loading }) => ({
  '& thead th:first-of-type': {
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 1.5)
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 0.75)
    }
  },
  '& tbody td:first-of-type': {
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 1.5)
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 0.75)
    }
  },
  ...(loading && {
    '& tbody tr': {
      filter: 'blur(5px)',
      pointerEvents: 'none'
    },

    '& tbody th, & tbody td': {
      animation: `${blink} 1.5s alternate infinite linear`
    }
  })
}));

export const UsersTableCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'withoutHover'
})(({ theme, withoutHover }) => ({
  border: 0,

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 0, 0.75, 1.5)
  },

  '&:last-child': {
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  },

  '&:hover': {
    cursor: 'pointer'
  },

  '&[scope="row"]': {
    height: 44
  },

  // tableCellType styles
  cursor: 'default',
  color: '#828282',

  ...(withoutHover && {
    '&:hover': {
      backgroundColor: 'transparent !important',
      cursor: 'default'
    }
  })
}));

export const RootProjectTableCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'withoutHover'
})(({ theme, withoutHover }) => ({
  border: 0,

  width: '35%',
  maxWidth: '35%',

  color: '#828282',
  cursor: 'default',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 0, 0.75, 1.5)
  },

  '&:last-child': {
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  },

  '&[scope="row"]': {
    height: 44
  },

  '&:hover': {
    cursor: 'default'
  },

  ...(withoutHover && {
    '&:hover': {
      backgroundColor: 'transparent !important',
      cursor: 'default'
    }
  })
}));

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: prop =>
    prop !== 'withoutHover' && prop !== 'disabledrow'
})(({ withoutHover, disabledrow }) => ({
  ...(disabledrow && {
    pointerEvents: 'none',
    opacity: 0.5
  }),

  ...(withoutHover && {
    '&:hover': {
      backgroundColor: 'transparent !important',
      cursor: 'default'
    }
  })
}));

export const RowTableCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'withoutHover'
})(({ theme, withoutHover }) => ({
  border: 0,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 0, 0.75, 1.5)
  },

  '&:last-child': {
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  },

  '&:hover': {
    cursor: 'pointer'
  },

  '&[scope="row"]': {
    height: 44
  },

  ...(withoutHover && {
    '&:hover': {
      backgroundColor: 'transparent !important',
      cursor: 'default'
    }
  })
}));

export const TitleCellCnt = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  '&.disabledBy': {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12
  }
}));

export const StyledPersonIcon = styled(Person, {
  shouldForwardProp: prop => prop !== 'disableduser'
})(({ theme, disableduser }) => ({
  color: getContrastColor(),
  verticalAlign: 'middle',
  marginRight: theme.spacing(),
  marginBottom: 2,

  ...(disableduser && {
    color: '#828282',
    opacity: 0.5
  })
}));

export const CheckboxTableCell = styled(TableCell)(({ theme }) => ({
  border: 0,
  verticalAlign: 'top',

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 0, 0.75, 1.5)
  },

  '&:last-child': {
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0
    }
  },

  '&:hover': {
    cursor: 'pointer'
  },

  '&[scope="row"]': {
    height: 44
  }
}));

export const TableCheckbox = styled(TableCell)(({ theme }) => ({
  marginTop: 10,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 1.5)
  }
}));
